import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CAMPAIGN_STATUSES, CAMPAIGN_STATUS_LABELS, type Campaign, type MarketingOverview, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { useSession } from '../../session';
import { CampaignBadge, day } from './shared';
import './marketing.css';

const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** Anmeldungen und Abmeldungen der letzten sechs Monate als Balken */
function SubscriberChart({ months }: { months: MarketingOverview['months'] }) {
  const max = Math.max(1, ...months.flatMap((m) => [m.subscribed, m.unsubscribed]));
  return (
    <>
      <div className="mk-chart" role="img" aria-label="Neue Anmeldungen und Abmeldungen der letzten sechs Monate">
        {months.map((m) => (
          <div key={m.month} className="mk-chart__col" title={`${MONTH_SHORT[Number(m.month.slice(5, 7)) - 1]} ${m.month.slice(0, 4)}: ${m.subscribed} Anmeldungen, ${m.unsubscribed} Abmeldungen`}>
            <div className="mk-chart__bars">
              <span className="mk-chart__bar mk-chart__bar--in" style={{ height: `${(m.subscribed / max) * 100}%` }} />
              <span className="mk-chart__bar mk-chart__bar--out" style={{ height: `${(m.unsubscribed / max) * 100}%` }} />
            </div>
            <span className="mk-chart__num">{m.subscribed} / {m.unsubscribed}</span>
            <span className="mk-chart__label">{MONTH_SHORT[Number(m.month.slice(5, 7)) - 1]}</span>
          </div>
        ))}
      </div>
      <ul className="mk-legend"><li><span className="mk-swatch mk-swatch--in" /> Anmeldungen</li><li><span className="mk-swatch mk-swatch--out" /> Abmeldungen</li></ul>
    </>
  );
}

/**
 * Marketing (KON-170): Kennzahlen zu den Abonnenten, die Bilanz der letzten Versände und die Liste der Kampagnen. Ein Öffnungs- und Klick-Tracking gibt es bewusst nicht,
 * die Bilanz zeigt Empfänger, gesendete, fehlgeschlagene Mails und Abmeldungen.
 */
export function MarketingPage() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? 1);
  const overview = useQuery({ queryKey: ['marketing-overview'], queryFn: async () => (await api<{ data: MarketingOverview }>('GET', '/marketing/overview')).data, refetchInterval: (q) => (q.state.data?.recent.some((r) => r.status === 'sending') ? 3000 : false) });
  const list = useQuery({
    queryKey: ['marketing-campaigns', { status, page }],
    queryFn: () => api<Paginated<Campaign>>('GET', `/marketing/campaigns${queryString({ status, page, perPage: 20 })}`),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.data.some((c) => c.status === 'sending') ? 3000 : false),
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? merged.set(k, v) : merged.delete(k));
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const o = overview.data;

  return (
    <>
      <div className="page-head">
        <h1>Marketing</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} Kampagnen` : ''}</span>
        <Link to="/marketing/abonnenten" className="btn btn--secondary">Abonnenten</Link>
        {can('marketing.write') && <Link to="/marketing/kampagnen/neu" className="btn btn--primary">Neue Kampagne</Link>}
      </div>
      {o && !o.mailEnabled && <div className="alert alert--warn" role="status">Der E-Mail-Versand ist auf diesem Server nicht eingerichtet. Kampagnen lassen sich anlegen, aber nicht versenden.</div>}
      {o && (
        <div className="mk-tiles">
          <div className="card mk-tile"><span className="muted small">Angemeldete Abonnenten</span><strong>{o.subscribers.subscribed.toLocaleString('de-DE')}</strong><span className="muted small">mit Einwilligung, erreichbar</span></div>
          <div className="card mk-tile"><span className="muted small">Bestätigung offen</span><strong className={o.subscribers.pending > 0 ? 'mk-warn' : ''}>{o.subscribers.pending}</strong><span className="muted small">noch kein Newsletter</span></div>
          <div className="card mk-tile"><span className="muted small">Abgemeldet</span><strong>{o.subscribers.unsubscribed}</strong><span className="muted small">bleiben gesperrt</span></div>
          <div className="card mk-tile"><span className="muted small">Mails in diesem Jahr</span><strong>{o.sentThisYear.toLocaleString('de-DE')}</strong><span className="muted small">{o.campaigns.sent} Kampagnen versendet</span></div>
        </div>
      )}
      {o && (
        <div className="mk-grid">
          <section className="card" aria-labelledby="mk-growth">
            <h2 id="mk-growth">Abonnenten, letzte 6 Monate</h2>
            <SubscriberChart months={o.months} />
          </section>
          <section className="card" aria-labelledby="mk-recent">
            <h2 id="mk-recent">Letzte Versände</h2>
            {o.recent.length === 0 ? (
              <p className="muted small">Noch keine Kampagne versendet.</p>
            ) : (
              <ul className="mk-recent">
                {o.recent.map((r) => (
                  <li key={r.id}>
                    <div className="mk-recent__head"><Link to={`/marketing/kampagnen/${r.id}`} className="strong-link">{r.name}</Link> <CampaignBadge status={r.status} /></div>
                    <div className="muted small">
                      {r.stats.sent} von {r.stats.recipients} gesendet{r.stats.failed > 0 && <span className="mk-bad"> · {r.stats.failed} fehlgeschlagen</span>} · {r.stats.unsubscribed} Abmeldungen
                      {r.stats.sent > 0 && <> ({((r.stats.unsubscribed / r.stats.sent) * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %)</>} · {day(r.finishedAt)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small">Es gibt kein Tracking von Öffnungen und Klicks. Das schützt die Privatsphäre der Empfänger und kommt ohne zusätzliche Einwilligung aus.</p>
          </section>
        </div>
      )}
      <label className="year-filter mk-filter">
        <span>Stand</span>
        <select className="input" value={status} onChange={(e) => update({ status: e.target.value })} aria-label="Stand der Kampagnen">
          <option value="">Alle</option>
          {CAMPAIGN_STATUSES.map((s) => <option key={s} value={s}>{CAMPAIGN_STATUS_LABELS[s]}</option>)}
        </select>
      </label>
      {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr><th>Kampagne</th><th>Stand</th><th className="num">Empfänger</th><th className="num">Gesendet</th><th className="num">Abmeldungen</th><th>Angelegt</th></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={7} className="empty">{status ? 'Keine Kampagnen mit diesem Stand.' : 'Noch keine Kampagne. Legen Sie die erste an, sobald Sie Abonnenten haben.'}</td></tr>}
            {list.data?.data.map((c) => (
              <ExpandRow
                key={c.id}
                summary={<Summary title={<Link to={`/marketing/kampagnen/${c.id}`} className="strong-link">{c.name}</Link>} aside={<CampaignBadge status={c.status} />} line2={c.subject} line3={c.stats.recipients > 0 ? `${c.stats.sent} von ${c.stats.recipients} gesendet · ${c.stats.unsubscribed} Abmeldungen` : `Angelegt ${day(c.createdAt)}`} />}
              >
                <td className="cell-desktop-title"><Link to={`/marketing/kampagnen/${c.id}`} className="strong-link">{c.name}</Link><div className="muted small">{c.subject}</div></td>
                <td data-label="Stand"><CampaignBadge status={c.status} /></td>
                <td data-label="Empfänger" className="num">{c.stats.recipients || '–'}</td>
                <td data-label="Gesendet" className="num">{c.stats.recipients ? <>{c.stats.sent}{c.stats.failed > 0 && <span className="mk-bad small"> ({c.stats.failed} Fehler)</span>}</> : '–'}</td>
                <td data-label="Abmeldungen" className="num">{c.stats.recipients ? c.stats.unsubscribed : '–'}</td>
                <td data-label="Angelegt">{day(c.createdAt)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
